import React, { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { useMobileContext } from '../../contexts/MobileContext';
import {
  APP_BAR_PAGES,
  MOBILE_MENU_SECTIONS,
  SITE_NAV_GROUPS,
} from '../../utils/productNavigation';
import PopupModalComponent from '../PopupModal/PopupModalComponent';
import { pushOtpDismissed } from '../../utils/dataLayer';
import {
  getPersonPreFill,
  profileRetrievePerson,
  profileRetrievePropertyDetails,
  profileRetrievePetDetails,
  profileRetrieveVehicles,
  profileRetrieveVehicleDrivers,
  profileSendOTPViaSMS,
  profileSendOTPViaEmail,
  profileValidateOTP,
} from '../../api/apiServices';
import {
  syncAccountData,
  readAccountDataCache,
} from '../../utils/accountDataCache';
import { updateFormValuesFromApi } from '../../utils/returningUserApiMapper';
import {
  AUTH_SESSION_UPDATED_EVENT,
  buildEnterEmailOtpModal,
  buildEnterOtpModal,
  buildPolicyHolderName,
  buildRequestOtpModal,
  isValidSaContactNumber,
  isValidatedWithin,
  normalizeCellNumber,
  persistAuthSession,
  persistReturnUserDetails,
  resolveStoredPolicyHolderName,
  resolveStoredProfileCellNumber,
  safeParse,
  ENABLE_OTP_SMS,
} from '../../utils/questionEngineOtpHelpers';
import { useFlag } from '../../experiments/flags';
import { mergePrefillIntoFormValues } from '../../utils/questionEnginePrefillHelpers';
import { toTitleCase } from '../../utils/casing';
import './AppBarStyles.css';
import {
  APP_BAR_LOGO as logo,
  APP_BAR_MENU_ICON as menuIcon,
} from './appBarAssets';

const signInIcon = 'https://assets.askhippo.co.za/Icons/nav/Union.svg';
const HIDDEN_MOBILE_CATEGORY_TITLES = new Set([
  'Motor',
  'Health',
  'Life',
  'Money',
  'Business',
]);

// Helper: get groups by category for mega menu / mobile menu
const getGroupsForCategory = (category) => {
  if (category === 'Compare All') return SITE_NAV_GROUPS;
  return SITE_NAV_GROUPS.filter((group) => group.navCategory === category);
};

const getNavAccountLabel = (fullName) =>
  String(fullName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)[0] || '';

// The bar's layout and styles (heights, gutters, logo, nav) are mirrored in
// src/staticHero/staticHero.css (docs/static-hero.md): change both, or landing
// pages jump when React takes over.
function ResponsiveAppBar() {
  const { isMobile, isTablet } = useMobileContext();
  // `otp_sms` is a held flag: this is `undefined` until the remote value is
  // known, so the sign-in button renders nothing rather than appearing on the
  // code default and disappearing once the document lands. The handlers below
  // keep reading ENABLE_OTP_SMS, which is settled by the time one can fire.
  const otpSmsEnabled = useFlag('otp_sms');
  const isMobileOrTablet = isMobile || isTablet;
  const navigate = useNavigate();
  const location = useLocation();

  const [showMegaMenu, setShowMegaMenu] = useState(false);
  const [activeCategory, setActiveCategory] = useState('Compare All');
  const [dropdownLeft, setDropdownLeft] = useState(null);
  const navItemRefs = useRef({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMobileSection, setExpandedMobileSection] = useState(null);
  const [accountLabel, setAccountLabel] = useState(() =>
    resolveStoredPolicyHolderName()
  );
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpModalData, setOtpModalData] = useState(null);
  const [otpStage, setOtpStage] = useState('idle');
  const [otpChannel, setOtpChannel] = useState('phone');
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpResetCounter, setOtpResetCounter] = useState(0);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);

  // Smooth scroll, used for in-page UX (e.g. opening the mega menu).
  const scrollToTop = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Centralised: navigate, close any menus, then scroll to top.
  // Same-path click → smooth scroll up.
  // Different-path click → flushSync forces the DOM swap synchronously and
  // scrollTo runs in the same task, so the browser paints the new page at the
  // top in a single frame instead of "scroll old page → swap content".
  const navigateAndResetUI = (href) => {
    setShowMegaMenu(false);
    setActiveCategory('Compare All');
    setMobileMenuOpen(false);
    setExpandedMobileSection(null);

    if (href === location.pathname) {
      setTimeout(scrollToTop, 0);
      return;
    }

    if (typeof window === 'undefined') {
      navigate(href);
      return;
    }

    flushSync(() => {
      navigate(href);
    });
    window.scrollTo(0, 0);
  };

  const handleOpenNavMenu = () => {
    setMobileMenuOpen(true);
  };

  const handleCloseNavMenu = () => {
    setMobileMenuOpen(false);
    setExpandedMobileSection(null);
  };

  // Desktop hover: any tab with products uses the mega menu
  const handleNavItemEnter = (page) => {
    if (isMobile) return;

    const groups = getGroupsForCategory(page);
    if (groups.length === 0) {
      setShowMegaMenu(false);
      return;
    }

    const el = navItemRefs.current[page];
    if (el) {
      const rect = el.getBoundingClientRect();
      setDropdownLeft(rect.left + rect.width / 2);
    }

    setActiveCategory(page);
    setShowMegaMenu(true);
  };

  const handleMobileSectionToggle = (section) => {
    if (expandedMobileSection === section) {
      setExpandedMobileSection(null);
    } else {
      setExpandedMobileSection(section);
    }
  };

  const firstNameHint =
    safeParse(localStorage.getItem('formValues'), {})?.FirstName || '';

  const closeOtpModal = useCallback(() => {
    setOtpModalOpen(false);
    setOtpStage('idle');
    setOtpError('');
  }, []);

  const persistProfileIdentity = useCallback(
    (person, cellNumber, extras = {}) => {
      const profileName = buildPolicyHolderName(person);
      const existingReturnUserDetails = safeParse(
        localStorage.getItem('returnUserDetails'),
        {}
      );

      persistReturnUserDetails({
        ...existingReturnUserDetails,
        ...person,
        ...extras,
        cellNumber,
      });
      persistAuthSession({
        cellNumber,
        profileName,
      });
      setAccountLabel(profileName);
      return profileName;
    },
    []
  );

  const applyIdPrefillToStoredFormValues = useCallback(
    async (returnUserDetails, baseFormValues = {}) => {
      const idNumber = String(
        returnUserDetails?.IDNumber || returnUserDetails?.idNumber || ''
      ).trim();

      if (!idNumber) return baseFormValues;

      const anchor = localStorage.getItem('idNumberAnchor') || '';
      if (anchor === idNumber) return baseFormValues;

      try {
        const prefillData = await getPersonPreFill(idNumber);
        const nextFormValues = mergePrefillIntoFormValues(
          prefillData,
          baseFormValues
        );

        localStorage.setItem('formValues', JSON.stringify(nextFormValues));
        return nextFormValues;
      } catch (error) {
        return baseFormValues;
      } finally {
        localStorage.setItem('idNumberAnchor', idNumber);
      }
    },
    []
  );

  const syncAccountLabel = useCallback(() => {
    if (!isValidatedWithin()) {
      setAccountLabel('');
      setAccountMenuAnchor(null);
      return;
    }

    setAccountLabel(resolveStoredPolicyHolderName());
  }, []);

  const sendOtpToCellNumber = useCallback(
    async (cellNumber) => {
      const normalizedCellNumber = normalizeCellNumber(cellNumber);

      await profileSendOTPViaSMS(normalizedCellNumber, firstNameHint);
      persistAuthSession({
        cellNumber: normalizedCellNumber,
        otp: null,
        requestedAt: Date.now(),
      });
      setOtpError('');
      setOtpModalData(buildEnterOtpModal(normalizedCellNumber, firstNameHint));
      setOtpStage('enter');
      setOtpModalOpen(true);
    },
    [firstNameHint]
  );

  const sendOtpToEmail = useCallback(
    async (email) => {
      const trimmedEmail = String(email || '').trim();

      await profileSendOTPViaEmail(trimmedEmail, firstNameHint);
      persistAuthSession({
        email: trimmedEmail,
        otp: null,
        requestedAt: Date.now(),
      });
      setOtpError('');
      setOtpModalData(buildEnterEmailOtpModal(trimmedEmail, firstNameHint));
      setOtpStage('enter');
      setOtpModalOpen(true);
    },
    [firstNameHint]
  );

  const completeOtpLogin = useCallback(
    async (cellNumber, otp) => {
      const normalizedCellNumber = normalizeCellNumber(cellNumber);

      const validateResult = await profileValidateOTP({
        cellNumber: normalizedCellNumber,
        otp,
      });
      const validatedEntry = Array.isArray(validateResult)
        ? validateResult[0]
        : null;
      if (!validatedEntry?.Validated) {
        throw new Error('OTP validation failed');
      }
      // Store JWT immediately so subsequent profile calls are authenticated
      persistAuthSession({
        jwtToken: validatedEntry.JWTToken,
        jwtValidTo: validatedEntry.ValidToDateUTC,
        otpVia: 'sms',
      });

      const person = await profileRetrievePerson(normalizedCellNumber);
      if (
        !person ||
        (typeof person === 'object' && Object.keys(person).length === 0)
      ) {
        throw new Error('Profile retrieval failed');
      }

      const [vehiclesRes, driversRes, propertiesRes] = await Promise.all([
        profileRetrieveVehicles(normalizedCellNumber),
        profileRetrieveVehicleDrivers(normalizedCellNumber),
        profileRetrievePropertyDetails(normalizedCellNumber),
      ]);
      const pets = await profileRetrievePetDetails(normalizedCellNumber).catch(
        () => null
      );

      const returnUserDetails = {
        ...person,
        Vehicles: vehiclesRes?.Vehicles || [],
        Drivers: driversRes?.Drivers || [],
        Properties: propertiesRes || null,
        Pets: pets?.Pets || [],
      };

      const mergedFormValues = updateFormValuesFromApi(returnUserDetails);
      await applyIdPrefillToStoredFormValues(
        returnUserDetails,
        mergedFormValues || {}
      );

      persistProfileIdentity(person, normalizedCellNumber, {
        Vehicles: vehiclesRes?.Vehicles || [],
        Drivers: driversRes?.Drivers || [],
        Properties: propertiesRes || null,
        Pets: pets?.Pets || [],
      });
      persistAuthSession({
        cellNumber: normalizedCellNumber,
        otp,
        validatedAt: Date.now(),
      });
      const pickIdNumber = (obj) =>
        [obj?.IDNumber, obj?.IdNumber, obj?.idNumber].find(
          (v) => typeof v === 'string' && v.trim().length > 0
        ) || '';
      const fv = safeParse(localStorage.getItem('formValues'), {});
      const resolvedIdNumber =
        pickIdNumber(returnUserDetails) || pickIdNumber(fv);

      void syncAccountData(normalizedCellNumber, {
        seedPerson: {
          ...returnUserDetails,
          IDNumber: resolvedIdNumber,
        },
        seedVehicles: vehiclesRes?.Vehicles || [],
        seedDrivers: driversRes?.Drivers || [],
        seedProperties: propertiesRes,
        seedPets: pets?.Pets || [],
      }).catch(() => {});
      setOtpError('');
      setOtpModalOpen(false);
      setOtpStage('idle');
      navigate('/accounts');
      return true;
    },
    [applyIdPrefillToStoredFormValues, persistProfileIdentity]
  );

  const completeOtpLoginViaEmail = useCallback(
    async (email, otp) => {
      const trimmedEmail = String(email || '').trim();

      const validateResult = await profileValidateOTP({
        email: trimmedEmail,
        otp,
      });
      const validatedEntry = Array.isArray(validateResult)
        ? validateResult[0]
        : null;
      if (!validatedEntry?.Validated) {
        throw new Error('OTP validation failed');
      }
      // Store JWT immediately so subsequent profile calls are authenticated
      persistAuthSession({
        jwtToken: validatedEntry.JWTToken,
        jwtValidTo: validatedEntry.ValidToDateUTC,
        otpVia: 'email',
      });

      const person = await profileRetrievePerson(trimmedEmail);
      if (
        !person ||
        (typeof person === 'object' && Object.keys(person).length === 0)
      ) {
        throw new Error('Profile retrieval failed');
      }

      const normalizedCellNumber = normalizeCellNumber(
        person?.CellNumber || ''
      );

      const [vehiclesRes, driversRes, propertiesRes] = await Promise.all([
        profileRetrieveVehicles(trimmedEmail),
        profileRetrieveVehicleDrivers(trimmedEmail),
        profileRetrievePropertyDetails(trimmedEmail),
      ]);
      const pets = await profileRetrievePetDetails(trimmedEmail).catch(
        () => null
      );

      const returnUserDetails = {
        ...person,
        Vehicles: vehiclesRes?.Vehicles || [],
        Drivers: driversRes?.Drivers || [],
        Properties: propertiesRes || null,
        Pets: pets?.Pets || [],
      };

      const mergedFormValues = updateFormValuesFromApi(returnUserDetails);
      await applyIdPrefillToStoredFormValues(
        returnUserDetails,
        mergedFormValues || {}
      );

      persistProfileIdentity(person, normalizedCellNumber, {
        Vehicles: vehiclesRes?.Vehicles || [],
        Drivers: driversRes?.Drivers || [],
        Properties: propertiesRes || null,
        Pets: pets?.Pets || [],
      });
      persistAuthSession({
        email: trimmedEmail,
        cellNumber: normalizedCellNumber,
        otp,
        validatedAt: Date.now(),
      });
      const pickIdNumber = (obj) =>
        [obj?.IDNumber, obj?.IdNumber, obj?.idNumber].find(
          (v) => typeof v === 'string' && v.trim().length > 0
        ) || '';
      const fv = safeParse(localStorage.getItem('formValues'), {});
      const resolvedIdNumber =
        pickIdNumber(returnUserDetails) || pickIdNumber(fv);

      void syncAccountData(normalizedCellNumber, {
        seedPerson: {
          ...returnUserDetails,
          IDNumber: resolvedIdNumber,
        },
        seedVehicles: vehiclesRes?.Vehicles || [],
        seedDrivers: driversRes?.Drivers || [],
        seedProperties: propertiesRes,
        seedPets: pets?.Pets || [],
      }).catch(() => {});
      setOtpError('');
      setOtpModalOpen(false);
      setOtpStage('idle');
      navigate('/accounts');
      return true;
    },
    [applyIdPrefillToStoredFormValues, persistProfileIdentity]
  );

  const handleLogout = useCallback(() => {
    localStorage.clear();
    sessionStorage.clear();
    setAccountMenuAnchor(null);
    setAccountLabel('');
    window.location.href = '/';
  }, []);

  const buildSignInRequestModal = useCallback(
    (via = 'phone') => {
      const requestModal = buildRequestOtpModal(firstNameHint, { via });
      // SMS is disabled — don't offer a path back to the phone flow.
      const channelToggleButton =
        via === 'email'
          ? ENABLE_OTP_SMS
            ? {
                index: 1,
                id: 'use-phone-instead',
                type: 'secondary-gray',
                text: 'Use phone number instead',
              }
            : null
          : {
              index: 1,
              id: 'use-email-instead',
              type: 'secondary-gray',
              text: 'Use email instead',
            };

      return {
        ...requestModal,
        buttons: requestModal.buttons
          .filter((btn) => btn.id !== 'first-time-quoting')
          .concat([
            ...(channelToggleButton ? [channelToggleButton] : []),
            ...(firstNameHint
              ? [
                  {
                    index: 2,
                    id: 'no-thats-not-me',
                    type: 'secondary-gray',
                    text: "No, that's not me",
                  },
                ]
              : []),
            {
              index: 3,
              id: 'cancel-otp',
              type: 'secondary-gray',
              text: 'Cancel',
              mt: firstNameHint ? 1 : 0,
            },
          ]),
      };
    },
    [firstNameHint]
  );

  const handleSignInClick = useCallback(
    (event) => {
      setMobileMenuOpen(false);
      setExpandedMobileSection(null);
      setShowMegaMenu(false);

      if (isValidatedWithin()) {
        setAccountMenuAnchor(event.currentTarget);
        return;
      }

      // SMS is disabled — default straight to the email flow.
      const defaultChannel = ENABLE_OTP_SMS ? 'phone' : 'email';
      setOtpError('');
      setOtpChannel(defaultChannel);
      setOtpModalData(buildSignInRequestModal(defaultChannel));
      setOtpStage('request');
      setOtpModalOpen(true);
    },
    [buildSignInRequestModal]
  );

  const handleOtpModalAction = useCallback(
    async (id, formState) => {
      // Only the opt-out buttons are tagged; pushOtpDismissed ignores the rest.
      pushOtpDismissed({
        buttonId: id,
        location: 'sign_in',
        // The header is on every page; GA4's page_location has the exact URL.
        pageType: location.pathname === '/' ? 'home' : 'other',
        stage: otpStage,
        channel: otpChannel,
      });

      if (id === 'use-email-instead') {
        setOtpError('');
        setOtpChannel('email');
        setOtpModalData(buildSignInRequestModal('email'));
        return;
      }

      if (id === 'use-phone-instead') {
        if (!ENABLE_OTP_SMS) return;
        setOtpError('');
        setOtpChannel('phone');
        setOtpModalData(buildSignInRequestModal('phone'));
        return;
      }

      if (id === 'send-otp') {
        if (otpSubmitting) return;

        if (otpChannel === 'email') {
          const enteredEmail = formState?.email_address;
          if (!String(enteredEmail || '').trim()) return;

          try {
            setOtpSubmitting(true);
            await sendOtpToEmail(enteredEmail);
          } finally {
            setOtpSubmitting(false);
          }
          return;
        }

        const enteredCellNumber =
          formState?.contact_number || resolveStoredProfileCellNumber();

        if (!String(enteredCellNumber || '').trim()) return;
        if (!isValidSaContactNumber(enteredCellNumber)) return;

        try {
          setOtpSubmitting(true);
          await sendOtpToCellNumber(enteredCellNumber);
        } finally {
          setOtpSubmitting(false);
        }
        return;
      }

      if (id === 'submit-otp') {
        // OTP digits complete — onComplete drives the actual validation.
        // Do nothing here so the modal stays open with the loading indicator.
        return;
      }

      if (id === 'no-thats-not-me') {
        localStorage.clear();
        sessionStorage.clear();
        closeOtpModal();
        window.location.href = '/';
        return;
      }

      closeOtpModal();
    },
    [
      closeOtpModal,
      location.pathname,
      otpChannel,
      otpStage,
      otpSubmitting,
      buildSignInRequestModal,
      sendOtpToCellNumber,
      sendOtpToEmail,
    ]
  );

  const handleOtpModalComplete = useCallback(
    async (formState) => {
      if (otpStage !== 'enter' || otpSubmitting) return;

      const otp = (formState?.otp_code ?? '').toString().trim();
      if (!/^\d{4}$/.test(otp)) return;

      const session = safeParse(localStorage.getItem('authSession'), {}) || {};

      if (otpChannel === 'email') {
        const email = session?.email || formState?.email_address;
        if (!email) return;

        try {
          setOtpSubmitting(true);
          await completeOtpLoginViaEmail(email, otp);
        } catch (error) {
          setOtpError('Invalid code. Please try again.');
          setOtpResetCounter((count) => count + 1);
          setOtpModalData(buildEnterEmailOtpModal(email, firstNameHint));
          setOtpStage('enter');
          setOtpModalOpen(true);
        } finally {
          setOtpSubmitting(false);
        }
        return;
      }

      const cellNumber =
        session?.cellNumber ||
        resolveStoredProfileCellNumber() ||
        formState?.contact_number;

      if (!cellNumber) return;

      try {
        setOtpSubmitting(true);
        await completeOtpLogin(cellNumber, otp);
      } catch (error) {
        setOtpError('Invalid code. Please try again.');
        setOtpResetCounter((count) => count + 1);
        setOtpModalData(buildEnterOtpModal(cellNumber, firstNameHint));
        setOtpStage('enter');
        setOtpModalOpen(true);
      } finally {
        setOtpSubmitting(false);
      }
    },
    [
      completeOtpLogin,
      completeOtpLoginViaEmail,
      firstNameHint,
      otpChannel,
      otpStage,
      otpSubmitting,
    ]
  );

  useEffect(() => {
    syncAccountLabel();

    const handleAuthSessionUpdated = () => {
      syncAccountLabel();
    };

    window.addEventListener(
      AUTH_SESSION_UPDATED_EVENT,
      handleAuthSessionUpdated
    );
    window.addEventListener('storage', handleAuthSessionUpdated);

    return () => {
      window.removeEventListener(
        AUTH_SESSION_UPDATED_EVENT,
        handleAuthSessionUpdated
      );
      window.removeEventListener('storage', handleAuthSessionUpdated);
    };
  }, [syncAccountLabel]);

  useEffect(() => {
    const syncIfSessionExpired = () => {
      syncAccountLabel();
    };

    const intervalId = window.setInterval(syncIfSessionExpired, 15000);

    document.addEventListener('visibilitychange', syncIfSessionExpired);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', syncIfSessionExpired);
    };
  }, [syncAccountLabel]);

  useEffect(() => {
    const handleJwtExpired = () => {
      if (!ENABLE_OTP_SMS) return;
      const session = safeParse(localStorage.getItem('authSession'), {}) || {};
      const rawAuthVia = session?.otpVia === 'email' ? 'email' : 'phone';
      // SMS is disabled — always fall back to the email flow.
      const authVia = ENABLE_OTP_SMS ? rawAuthVia : 'email';

      setOtpError('');
      setOtpChannel(authVia);
      setOtpModalData(
        buildRequestOtpModal(firstNameHint, {
          via: authVia,
          returningUser: true,
        })
      );
      setOtpStage('request');
      setOtpModalOpen(true);
    };
    window.addEventListener('hippo:jwt-expired', handleJwtExpired);
    return () =>
      window.removeEventListener('hippo:jwt-expired', handleJwtExpired);
  }, [firstNameHint]);

  useEffect(() => {
    let isMounted = true;

    const hydrateSession = async () => {
      if (!isValidatedWithin()) return;

      // Resolve the lookup identifier from the channel the user actually
      // authenticated with — an email-based session may have no cellNumber.
      const session = safeParse(localStorage.getItem('authSession'), {}) || {};
      const isEmailAuth = session?.otpVia === 'email';
      const identifier = isEmailAuth
        ? session?.email || ''
        : resolveStoredProfileCellNumber();
      if (!identifier) return;

      try {
        const person = await profileRetrievePerson(identifier);
        if (!isMounted) return;

        if (
          person &&
          (typeof person !== 'object' || Object.keys(person).length > 0)
        ) {
          const resolvedCellNumber = isEmailAuth
            ? normalizeCellNumber(person?.CellNumber || '')
            : identifier;

          if (resolvedCellNumber) {
            persistProfileIdentity(person, resolvedCellNumber);
            // Seed the other slices from whatever's already cached so this
            // lightweight refresh doesn't trigger syncAccountData's own
            // fallback fetches — those would run against resolvedCellNumber
            // even for email-authenticated sessions, duplicating the
            // email-keyed calls already made by the silent return-user sync.
            const cachedSnapshot = readAccountDataCache(resolvedCellNumber);
            void syncAccountData(resolvedCellNumber, {
              seedPerson: person,
              seedVehicles: cachedSnapshot?.vehicles ?? [],
              seedDrivers: cachedSnapshot?.drivers ?? [],
              seedProperties: cachedSnapshot?.properties ?? null,
              seedPets: cachedSnapshot?.pets ?? [],
            }).catch(() => {});
          } else {
            setAccountLabel(buildPolicyHolderName(person));
          }
        } else {
          setAccountLabel('');
        }
      } catch (error) {
        if (isMounted) {
          setAccountLabel(resolveStoredPolicyHolderName());
        }
      }
    };

    hydrateSession();

    return () => {
      isMounted = false;
    };
  }, [persistProfileIdentity]);

  useEffect(() => {
    setShowMegaMenu(false);
    setMobileMenuOpen(false);
    setExpandedMobileSection(null);
    setAccountMenuAnchor(null);
  }, [isMobileOrTablet]);

  if (isMobileOrTablet) {
    return (
      <div className="navbar-wrapper">
        <div
          style={{
            height: '48px',
            backgroundColor: 'transparent',
          }}
        />
        <AppBar position="fixed" className="container" sx={{ height: '48px' }}>
          <Container maxWidth="xl">
            <Toolbar
              disableGutters
              sx={{ position: 'relative', minHeight: '48px !important' }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box',
                  px: '12px',
                }}
              >
                <Link
                  to="/"
                  style={{ textDecoration: 'none' }}
                  onClick={(e) => {
                    e.preventDefault();
                    navigateAndResetUI('/');
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 0 }}>
                    <img
                      src={logo}
                      alt="Logo"
                      className="mobile-logo"
                      width="1500"
                      height="326"
                    />
                  </Box>
                </Link>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {otpSmsEnabled && (
                    <button
                      type="button"
                      className="sign-in-container sign-in-container--mobile"
                      onClick={handleSignInClick}
                      aria-label={
                        accountLabel ? `${accountLabel} account` : 'Sign in'
                      }
                    >
                      <img
                        src={signInIcon}
                        alt="Sign in"
                        width={24}
                        height={24}
                        aria-hidden="true"
                        className="sign-in-logo"
                      />
                    </button>
                  )}
                  <IconButton
                    size="large"
                    aria-label="open navigation menu"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={
                      mobileMenuOpen ? handleCloseNavMenu : handleOpenNavMenu
                    }
                    color="inherit"
                    sx={{ p: 0 }}
                  >
                    {mobileMenuOpen ? (
                      <span style={{ fontSize: '24px', color: '#333' }}>✕</span>
                    ) : (
                      <img
                        src={menuIcon}
                        alt="Menu"
                        width={24}
                        height={24}
                        style={{ width: 24, height: 24 }}
                      />
                    )}
                  </IconButton>
                </Box>

                <Menu
                  anchorEl={accountMenuAnchor}
                  open={Boolean(accountMenuAnchor)}
                  onClose={() => setAccountMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 0.5,
                        borderRadius: '12px',
                        boxShadow:
                          '0px 1px 2px 0px rgba(162,172,177,0.2), 0px 12px 24px -4px rgba(22,47,59,0.12)',
                        minWidth: 160,
                      },
                    },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      setAccountMenuAnchor(null);
                      navigate('/accounts');
                    }}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.9375rem',
                      fontWeight: 500,
                      color: '#162f3b',
                      py: 1.25,
                      px: 2.5,
                      '&:hover': {
                        color: '#9ea900',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    My Account
                  </MenuItem>
                  <MenuItem
                    onClick={handleLogout}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.9375rem',
                      fontWeight: 500,
                      color: '#162f3b',
                      py: 1.25,
                      px: 2.5,
                      '&:hover': {
                        color: '#9ea900',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    Log Out
                  </MenuItem>
                </Menu>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>

        {mobileMenuOpen && (
          <div className="mobile-menu-overlay">
            <div className="mobile-menu-content">
              <div className="mobile-menu-items">
                {MOBILE_MENU_SECTIONS.map((section) => {
                  const groups = getGroupsForCategory(section);
                  const hasContent = groups.length > 0;

                  return (
                    <div key={section} className="mobile-menu-section">
                      <div
                        className="mobile-menu-section-header"
                        onClick={() => handleMobileSectionToggle(section)}
                      >
                        <span>{toTitleCase(section)}</span>
                        <span className="mobile-menu-arrow">
                          {expandedMobileSection === section ? (
                            <ArrowDropUpIcon
                              sx={{ fontSize: 24, color: '#162F3B' }}
                            />
                          ) : (
                            <ArrowDropDownIcon
                              sx={{ fontSize: 24, color: '#162F3B' }}
                            />
                          )}
                        </span>
                      </div>

                      {expandedMobileSection === section && (
                        <div className="mobile-menu-section-content">
                          {hasContent ? (
                            groups.map((group) => (
                              <div
                                key={group.title}
                                className="mobile-category"
                              >
                                {!HIDDEN_MOBILE_CATEGORY_TITLES.has(
                                  group.title
                                ) && (
                                  <div className="mobile-category-title">
                                    {toTitleCase(group.title)}
                                  </div>
                                )}
                                {group.links.map((item, index) => (
                                  <div
                                    key={index}
                                    className="mobile-category-item"
                                    onClick={() => {
                                      navigateAndResetUI(item.href);
                                    }}
                                  >
                                    {toTitleCase(item.label)}
                                  </div>
                                ))}
                              </div>
                            ))
                          ) : (
                            <div className="mobile-menu-placeholder">
                              {toTitleCase(section)} content coming soon...
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {otpModalOpen && otpModalData && (
          <PopupModalComponent
            data={otpModalData}
            open={otpModalOpen}
            onClose={closeOtpModal}
            onAction={handleOtpModalAction}
            onComplete={handleOtpModalComplete}
            isMobile={isMobile}
            loading={otpSubmitting}
            otpError={otpError}
            otpResetCounter={otpResetCounter}
          />
        )}
      </div>
    );
  }

  return (
    // This wrapper covers the navbar AND the mega menu
    // Leaving this whole area closes the mega menu cleanly.
    <div
      className="navbar-wrapper"
      onMouseLeave={() => {
        if (!isMobile) {
          setShowMegaMenu(false);
        }
      }}
    >
      <div
        style={{
          height: isMobile ? '48px' : '80px',
          backgroundColor: 'transparent',
        }}
      />
      <AppBar position="fixed" className="container">
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ position: 'relative' }}>
            {/* Desktop Logo */}
            <Link
              to="/"
              style={{ textDecoration: 'none' }}
              onClick={(e) => {
                e.preventDefault();
                navigateAndResetUI('/');
              }}
            >
              <Box sx={{ display: isMobile ? 'none' : 'flex', mr: 1 }}>
                <img
                  src={logo}
                  alt="Hippo car insurance comparison - Compare quotes from South Africa's top insurers"
                  title="Hippo car insurance comparison - Compare quotes from South Africa's top insurers"
                  className="logo"
                  width="1500"
                  height="326"
                  fetchPriority="high"
                />
              </Box>
            </Link>

            {/* Mobile Menu */}
            <Box
              sx={{
                flexGrow: 1,
                display: isMobile ? 'flex' : 'none',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                px: '12px',
              }}
            >
              <Link
                to="/"
                style={{ textDecoration: 'none' }}
                onClick={(e) => {
                  e.preventDefault();
                  navigateAndResetUI('/');
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 0 }}>
                  <img
                    src={logo}
                    alt="Logo"
                    className="mobile-logo"
                    width="1500"
                    height="326"
                  />
                </Box>
              </Link>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {otpSmsEnabled && (
                  <button
                    type="button"
                    className="sign-in-container sign-in-container--mobile"
                    onClick={handleSignInClick}
                    aria-label={
                      accountLabel ? `${accountLabel} account` : 'Sign in'
                    }
                  >
                    <img
                      src={signInIcon}
                      alt="Sign in"
                      width={24}
                      height={24}
                      aria-hidden="true"
                      className="sign-in-logo"
                    />
                  </button>
                )}
                <IconButton
                  size="large"
                  aria-label="open navigation menu"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={
                    mobileMenuOpen ? handleCloseNavMenu : handleOpenNavMenu
                  }
                  color="inherit"
                  sx={{ p: 0 }}
                >
                  {mobileMenuOpen ? (
                    <span style={{ fontSize: '24px', color: '#333' }}>✕</span>
                  ) : (
                    <img
                      src={menuIcon}
                      alt="Menu"
                      width={24}
                      height={24}
                      style={{ width: 24, height: 24 }}
                    />
                  )}
                </IconButton>
              </Box>
            </Box>

            {/* Desktop Navigation */}
            <Box
              sx={{
                flexGrow: 1,
                display: isMobile ? 'none' : 'flex',
                justifyContent: 'center',
              }}
            >
              {APP_BAR_PAGES.map((page) => (
                <div
                  key={page}
                  ref={(el) => {
                    navItemRefs.current[page] = el;
                  }}
                  className="nav-item-with-mega"
                  onMouseEnter={() => handleNavItemEnter(page)}
                >
                  <Button
                    className={`nav-link ${
                      activeCategory === page && showMegaMenu ? 'active' : ''
                    }`}
                    sx={{
                      my: 2,
                      color: '#333',
                      display: 'block',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                    onClick={() => {
                      scrollToTop();
                    }}
                  >
                    {toTitleCase(page)}
                  </Button>
                  {activeCategory === page &&
                    showMegaMenu &&
                    page !== 'Compare All' && (
                      <div
                        className="nav-item-dropdown"
                        style={{
                          left:
                            dropdownLeft != null ? `${dropdownLeft}px` : '50%',
                        }}
                      >
                        <h3 className="mega-menu-category">
                          {toTitleCase(
                            getGroupsForCategory(page)[0]?.title || page
                          )}
                        </h3>
                        <ul className="mega-menu-list">
                          {getGroupsForCategory(page)
                            .flatMap((g) => g.links)
                            .map((item, i) => (
                              <li key={i} className="mega-menu-item">
                                <a
                                  href={item.href}
                                  className="mega-menu-link"
                                  data-text={toTitleCase(item.label)}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    navigateAndResetUI(item.href);
                                  }}
                                >
                                  {toTitleCase(item.label)}
                                </a>
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                </div>
              ))}
            </Box>

            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                display: isMobile ? 'none' : 'flex',
                alignItems: 'center',
              }}
            >
              {otpSmsEnabled && (
                <button
                  type="button"
                  className="sign-in-container"
                  onClick={handleSignInClick}
                  aria-label={
                    accountLabel ? `${accountLabel} account` : 'Sign in'
                  }
                >
                  <img
                    src={signInIcon}
                    alt="Sign in"
                    width={24}
                    height={24}
                    aria-hidden="true"
                    className="sign-in-logo"
                  />
                  <span className="sign-in-link">
                    {getNavAccountLabel(accountLabel) || 'Sign In'}
                  </span>
                </button>
              )}
              <Menu
                anchorEl={accountMenuAnchor}
                open={Boolean(accountMenuAnchor)}
                onClose={() => setAccountMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 0.5,
                      borderRadius: '12px',
                      boxShadow:
                        '0px 1px 2px 0px rgba(162,172,177,0.2), 0px 12px 24px -4px rgba(22,47,59,0.12)',
                      minWidth: 160,
                    },
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    setAccountMenuAnchor(null);
                    navigate('/accounts');
                  }}
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.9375rem',
                    fontWeight: 500,
                    color: '#162f3b',
                    py: 1.25,
                    px: 2.5,
                    '&:hover': {
                      color: '#9ea900',
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  My Account
                </MenuItem>
                <MenuItem
                  onClick={handleLogout}
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.9375rem',
                    fontWeight: 500,
                    color: '#162f3b',
                    py: 1.25,
                    px: 2.5,
                    '&:hover': {
                      color: '#9ea900',
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  Log Out
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Desktop Mega Menu */}
      {showMegaMenu && !isMobile && activeCategory === 'Compare All' && (
        <div className="mega-menu">
          <Container maxWidth="xl">
            <div className="mega-menu-content">
              {getGroupsForCategory(activeCategory).map((group) => (
                <div key={group.title} className="mega-menu-column">
                  <h3 className="mega-menu-category">
                    {toTitleCase(group.title)}
                  </h3>
                  <ul className="mega-menu-list">
                    {group.links.map((item, index) => (
                      <li key={index} className="mega-menu-item">
                        <a
                          href={item.href}
                          className="mega-menu-link"
                          data-text={toTitleCase(item.label)}
                          onClick={(e) => {
                            e.preventDefault();
                            navigateAndResetUI(item.href);
                          }}
                        >
                          {toTitleCase(item.label)}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Container>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && isMobile && (
        <div className="mobile-menu-overlay">
          <div className="mobile-menu-content">
            <div className="mobile-menu-items">
              {MOBILE_MENU_SECTIONS.map((section) => {
                const groups = getGroupsForCategory(section);
                const hasContent = groups.length > 0;

                return (
                  <div key={section} className="mobile-menu-section">
                    <div
                      className="mobile-menu-section-header"
                      onClick={() => handleMobileSectionToggle(section)}
                    >
                      <span>{toTitleCase(section)}</span>
                      <span className="mobile-menu-arrow">
                        {expandedMobileSection === section ? (
                          <ArrowDropUpIcon
                            sx={{ fontSize: 24, color: '#162F3B' }}
                          />
                        ) : (
                          <ArrowDropDownIcon
                            sx={{ fontSize: 24, color: '#162F3B' }}
                          />
                        )}
                      </span>
                    </div>

                    {expandedMobileSection === section && (
                      <div className="mobile-menu-section-content">
                        {hasContent ? (
                          groups.map((group) => (
                            <div key={group.title} className="mobile-category">
                              {!HIDDEN_MOBILE_CATEGORY_TITLES.has(
                                group.title
                              ) && (
                                <div className="mobile-category-title">
                                  {toTitleCase(group.title)}
                                </div>
                              )}
                              {group.links.map((item, index) => (
                                <div
                                  key={index}
                                  className="mobile-category-item"
                                  onClick={() => {
                                    navigateAndResetUI(item.href);
                                  }}
                                >
                                  {toTitleCase(item.label)}
                                </div>
                              ))}
                            </div>
                          ))
                        ) : (
                          <div className="mobile-menu-placeholder">
                            {toTitleCase(section)} content coming soon...
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {otpModalOpen && otpModalData && (
        <PopupModalComponent
          data={otpModalData}
          open={otpModalOpen}
          onClose={closeOtpModal}
          onAction={handleOtpModalAction}
          onComplete={handleOtpModalComplete}
          isMobile={isMobile}
          loading={otpSubmitting}
          otpError={otpError}
          otpResetCounter={otpResetCounter}
        />
      )}
    </div>
  );
}

export default ResponsiveAppBar;
